import { Lightformer, useEnvironment } from "@react-three/drei";
import { createPortal, useFrame, useThree } from "@react-three/fiber";
import {
  Component,
  type ErrorInfo,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  type Camera,
  type CubeCamera,
  type Group,
  HalfFloatType,
  MathUtils,
  Scene,
  Vector3,
  WebGLCubeRenderTarget,
} from "three";
import { useEnvironmentStudio } from "../stores/environmentStudioStore";
import {
  DEFAULT_PRODUCT_VIEWER_ASSETS,
  useProductViewerAssets,
} from "./productViewerAssets";

/**
 * Studio HDRI that the product scenes are lit and reflected in.
 *
 * "Brown Photostudio 02" by Sergej Majboroda / Poly Haven (CC0), 1k Radiance
 * file served from the frontend's static directory.
 *
 * 1k on purpose: three's PMREM derives its cube size from the equirect width
 * (width / 4), so 1k gives 256px faces and 2k would give 512px — sharper
 * mirror reflections, but 6.5 MB instead of 1.6 MB on every page load. The
 * metals carry a roughness that matches real anodised aluminium instead, which
 * hides the difference.
 * @see https://polyhaven.com/a/brown_photostudio_02
 */
export const PRODUCT_ENVIRONMENT_HDRI =
  DEFAULT_PRODUCT_VIEWER_ASSETS.environment;

/**
 * Scales the studio's contribution — and it is the *only* dial for it. For a
 * `MeshStandardMaterial` without its own `envMap`, three overrides
 * `material.envMapIntensity` with `scene.environmentIntensity` at draw time
 * (WebGLRenderer), so per-material values would silently do nothing. Express
 * per-material differences through roughness and colour instead.
 */
export const PRODUCT_ENVIRONMENT_INTENSITY = 1;

/**
 * Yaw of the photographed studio around the product, in degrees. Its window
 * and lamps sit at fixed azimuths, so without this the room lands wherever
 * the panorama happens to point.
 *
 * -60° was picked by measuring mean luminance of the front panel, the side
 * panel, the lid and the corner fittings across a full sweep, from the viewer's
 * default camera: it gives the brightest front face (+28% over an unrotated
 * environment) and the brightest corner fittings while keeping the lid well
 * short of clipping (which a rotation around +150° blows out).
 */
export const PRODUCT_ENVIRONMENT_ROTATION_DEG = -60;

/**
 * Azimuth the studio is laid out for, in degrees from +z towards +x: the
 * softboxes below are placed relative to a viewer standing here. It is the
 * viewer's default camera — `ProductViewerXr` starts at (0.75, 1.125, 1.875),
 * 29° above the floor — and the cube is yawed every frame by however far the
 * camera has orbited from it, so the layout holds from every side.
 */
const VIEW_AZIMUTH_DEG = 22;

/** How far the softboxes sit from the product, in metres. Only their angular size matters. */
const SOFTBOX_DISTANCE_M = 4;

/**
 * Where the key softbox stands, relative to the viewer — high on their left.
 * Shared with {@link StudioShadow}, which casts the floor shadow from this
 * same direction so that the highlight on the case and the shadow beside it
 * agree on where the light is.
 */
export const STUDIO_KEY = { azimuthDeg: -45, elevationDeg: 50 } as const;

/**
 * How far the studio has turned from its laid-out azimuth to face the
 * camera, in radians. Zero when it is not following.
 *
 * @param forward Scratch vector, so the per-frame call allocates nothing.
 */
export function studioYaw(
  camera: Camera,
  followCamera: boolean,
  forward: Vector3,
): number {
  if (!followCamera) return 0;
  camera.getWorldDirection(forward);
  const viewerAzimuth = Math.atan2(-forward.x, -forward.z);
  return viewerAzimuth - MathUtils.degToRad(VIEW_AZIMUTH_DEG);
}

/** Unit vector from the product towards the key softbox, in world space, for a given yaw. */
export function studioKeyDirection(yaw: number, out: Vector3): Vector3 {
  const azimuth =
    MathUtils.degToRad(STUDIO_KEY.azimuthDeg + VIEW_AZIMUTH_DEG) + yaw;
  const elevation = MathUtils.degToRad(STUDIO_KEY.elevationDeg);
  return out.set(
    Math.cos(elevation) * Math.sin(azimuth),
    Math.sin(elevation),
    Math.cos(elevation) * Math.cos(azimuth),
  );
}

/**
 * Where a softbox goes, from the viewer's point of view.
 *
 * @param azimuthDeg Degrees around the product: 0 is straight behind the
 *   viewer, positive is to the viewer's right, 180 is behind the product.
 * @param elevationDeg Degrees above the floor plane; negative is below it,
 *   which an environment map is perfectly happy with.
 */
function softboxPosition(
  azimuthDeg: number,
  elevationDeg: number,
): [number, number, number] {
  const azimuth = MathUtils.degToRad(azimuthDeg + VIEW_AZIMUTH_DEG);
  const elevation = MathUtils.degToRad(elevationDeg);
  const flat = SOFTBOX_DISTANCE_M * Math.cos(elevation);
  return [
    flat * Math.sin(azimuth),
    SOFTBOX_DISTANCE_M * Math.sin(elevation),
    flat * Math.cos(azimuth),
  ];
}

interface StandardEnvironmentProps {
  /**
   * Edge length of the cube the studio is composited into. 512 keeps the
   * photographed room as sharp as its 1k source allows in the chrome.
   * @default 512
   */
  resolution?: number;

  /**
   * Kept for callers that pass it. The composited cube has always been
   * installed on the canvas's scene regardless — drei's `Environment` fell
   * back to the default scene when handed `undefined` — and still is.
   * @default true
   */
  isGlobal?: boolean;
}

/**
 * One standardised, reusable studio for every 3D renderer.
 *
 * It is the only light source in the product scenes: key, fill and
 * reflections all come out of this image-based light map, with no analytic
 * lights layered on top.
 *
 * The map is a composite, rendered once into a cube: the photographed studio
 * as the backdrop, with a few softboxes placed in front of it. The two do
 * different jobs and neither does the other's.
 *
 * The photograph is what a chrome rivet or a ball corner reflects — a room
 * with a window, lamps and furniture, which is what makes a small mirror-like
 * surface read as metal rather than as a grey blob. But half of the
 * photograph's energy is in two percent of its pixels, thin LED arcs on the
 * ceiling, so on a large glossy face it produces either a streak or nothing:
 * the phenolic lid showed no highlight at all.
 *
 * The softboxes are what a large glossy face reflects. They are placed where
 * the default camera's reflection lands: a vertical face mirrors the camera
 * about its normal, so the viewer sees the studio *below the horizon behind
 * themselves* in the front panel, and the lid mirrors the camera's 29° up
 * into the studio *behind the product* at the same elevation. One softbox
 * sits at each of those spots; a third, high and to the left, is the key that
 * gives the extrusions their gradient.
 *
 * Nothing here costs anything per frame: the cube is rendered once when the
 * HDRI arrives and PMREM-filtered by three on first use, exactly as the
 * photograph alone was.
 */
const StandardEnvironmentLoaded = ({
  files,
  resolution = 512,
}: StandardEnvironmentProps & { files: string }) => {
  const scene = useThree((state) => state.scene);
  const gl = useThree((state) => state.gl);
  const invalidate = useThree((state) => state.invalidate);
  const rotationOverride = useEnvironmentStudio((state) => state.rotationDeg);
  const softboxOverride = useEnvironmentStudio((state) => state.softboxes);
  const followOverride = useEnvironmentStudio((state) => state.followCamera);
  const rotationDeg = rotationOverride ?? PRODUCT_ENVIRONMENT_ROTATION_DEG;
  const softboxes = softboxOverride ?? true;
  const followCamera = followOverride ?? true;
  // Suspends until the file is in; the stage's Suspense boundary covers it.
  const studio = useEnvironment({ files });

  const [virtualScene] = useState(() => new Scene());
  const cubeCamera = useRef<CubeCamera>(null);
  const softboxGroup = useRef<Group>(null);
  const target = useMemo(() => {
    const fbo = new WebGLCubeRenderTarget(resolution);
    fbo.texture.type = HalfFloatType;
    fbo.texture.name = "ProductViewer.studio";
    return fbo;
  }, [resolution]);
  useEffect(() => () => target.dispose(), [target]);

  useLayoutEffect(() => {
    // The photograph is the virtual scene's background, so it fills whatever
    // the softboxes do not cover, and it is yawed there rather than through
    // `scene.environmentRotation` so that the softboxes keep their places
    // relative to the viewer.
    virtualScene.background = studio;
    virtualScene.backgroundRotation.set(0, MathUtils.degToRad(rotationDeg), 0);
    if (softboxGroup.current) softboxGroup.current.visible = softboxes;
    const autoClear = gl.autoClear;
    gl.autoClear = true;
    cubeCamera.current?.update(gl, virtualScene);
    gl.autoClear = autoClear;

    scene.environment = target.texture;
    scene.environmentIntensity = PRODUCT_ENVIRONMENT_INTENSITY;
    invalidate();
    return () => {
      if (scene.environment === target.texture) scene.environment = null;
    };
  }, [
    gl,
    scene,
    studio,
    target,
    virtualScene,
    invalidate,
    rotationDeg,
    softboxes,
  ]);

  // Turn the studio with the camera, so that the side facing the viewer is
  // always the one the softboxes were placed for. The whole cube turns —
  // photograph included — because it is one texture, and one yaw uniform is
  // what it costs; turning the softboxes alone would mean re-rendering and
  // re-filtering the cube on every orbit step, which is the one expensive
  // step in this file. Metals therefore look the same from every side, and
  // the studio does not slide across them while orbiting.
  //
  // The euler is the studio's apparent rotation: three negates it before
  // building the lookup matrix (WebGLMaterials), so a positive yaw here turns
  // the studio positively, from +z towards +x — the same sense the viewer's
  // azimuth is measured in.
  const forward = useMemo(() => new Vector3(), []);
  useFrame(({ camera }) => {
    scene.environmentRotation.y = studioYaw(camera, followCamera, forward);
  });

  return createPortal(
    <>
      {/* Shown or hidden by the effect above, immediately before it renders
          the cube, so the two can never disagree. */}
      <group ref={softboxGroup}>
        {/* Key: high on the viewer's left, for the gradient on the extrusions. */}
        <Lightformer
          form="rect"
          intensity={5}
          position={softboxPosition(
            STUDIO_KEY.azimuthDeg,
            STUDIO_KEY.elevationDeg,
          )}
          scale={[2.4, 1.8, 1]}
        />
        {/* Lid light: behind the product at the camera's elevation, so the
          lid mirrors it towards the viewer. Off-centre, so the highlight
          crosses the lid rather than sitting in its middle. */}
        <Lightformer
          form="rect"
          intensity={3}
          position={softboxPosition(200, 32)}
          scale={[3.2, 1.4, 1]}
        />
        {/* Front fill: low behind the viewer, where a vertical face mirrors the
          camera to. Broad and dim: it is the bounce card that lifts the
          front panel out of the floor's darkness. */}
        <Lightformer
          form="rect"
          intensity={1.4}
          position={softboxPosition(10, -22)}
          scale={[4, 2.2, 1]}
        />
      </group>
      <cubeCamera ref={cubeCamera} args={[0.1, 1000, target]} />
    </>,
    virtualScene,
  );
};

interface StudioHdriLoadErrorBoundaryProps {
  /** HDRI the environment currently loads: the dev override or the shipped one. */
  files: string;
  /** The shipped HDRI, loaded instead when `files` fails. */
  fallbackFiles: string;
  /** Renders the environment for a file; named `render…` as a render prop. */
  renderEnvironment: (files: string) => ReactNode;
}

/**
 * Keeps a failing dev override from taking the canvas down: the override
 * (dev Studio panel) is cleared and the shipped HDRI renders instead. Without
 * an override the boundary is inert, so a failing shipped HDRI reaches the
 * canvas as before and a preview capture fails instead of storing an unlit
 * image.
 */
export class StudioHdriLoadErrorBoundary extends Component<
  Readonly<StudioHdriLoadErrorBoundaryProps>,
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError(): { hasError: true } {
    return { hasError: true };
  }

  componentDidUpdate(prevProps: Readonly<StudioHdriLoadErrorBoundaryProps>) {
    if (prevProps.files !== this.props.files) {
      this.setState({ hasError: false });
    }
  }

  componentDidCatch(_error: Error, _info: ErrorInfo) {
    if (this.props.files !== this.props.fallbackFiles) {
      useEnvironmentStudio.getState().set({ hdri: null });
    }
  }

  render() {
    const { files, fallbackFiles, renderEnvironment } = this.props;
    // A failing shipped HDRI throws again here; React hands an error from a
    // boundary's own recovery render to the enclosing boundary.
    return renderEnvironment(this.state.hasError ? fallbackFiles : files);
  }
}

export const StandardEnvironment = ({
  resolution = 512,
}: StandardEnvironmentProps) => {
  const assets = useProductViewerAssets();
  const hdriOverride = useEnvironmentStudio((state) => state.hdri);
  const files = hdriOverride ?? assets.environment;

  return (
    <StudioHdriLoadErrorBoundary
      files={files}
      fallbackFiles={assets.environment}
      renderEnvironment={(environmentFiles) => (
        <StandardEnvironmentLoaded
          files={environmentFiles}
          resolution={resolution}
        />
      )}
    />
  );
};
